import { LoaderIcon } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { NegativePlaceholder } from '../shared/components/NegativePlaceholder';
import { useElementSize } from '../shared/hooks/useElementSize';

import { DirDrillingActionsBar } from './components/DirDrillingActionsBar';
import { ParameterLabels } from './components/ParameterLabels';
import { ToolChart } from './components/ToolChart';
import { MARGIN_RIGHT } from './consts';
import styles from './DirectionalDrillingMonitor.module.css';
import type { DirectionalDrillingState } from './store/DirectionalDrillingState';

export const DirectionalDrillingMonitor = observer(function DirectionalDrillingMonitor({
  widgetState,
}: {
  widgetState: DirectionalDrillingState;
}) {
  const { t, ready } = useTranslation('directionalDrillingWidget', { useSuspense: false });
  const bodyRef = useRef<HTMLDivElement>(null);
  const { width, height } = useElementSize(bodyRef);

  useEffect(() => {
    widgetState.updateBaseParams();
  }, [widgetState]);

  return (
    <div
      ref={bodyRef}
      className={clsx('go-widgets-widget-wrapper', `go-widget-theme-${widgetState.appParams.theme}`, styles.root)}
    >
      {!ready || !widgetState.fullyLoaded ? (
        <div className={styles.loader}>
          <LoaderIcon size="l" />
        </div>
      ) : widgetState.error || widgetState.gravityToolState?.error || widgetState.magneticToolState?.error ? (
        <NegativePlaceholder message="directionalDrillingWidget:loadError" />
      ) : widgetState.baseParamsIssue ? (
        <NegativePlaceholder
          message={`directionalDrillingWidget:${widgetState.baseParamsIssue}`}
          redirectLabel={t('redirectToBasicParameters')}
          redirectToBasicParameters={widgetState.eventHandlers.onRedirectToBasicParameters}
        />
      ) : (
        <div className={styles.chart}>
          <ToolChart widgetState={widgetState} width={Math.max(1, width - MARGIN_RIGHT)} height={height} />
          <ParameterLabels widgetState={widgetState} />
        </div>
      )}
      {ready && (
        <div
          className={clsx('go-widgets-actions-bar-wrapper', styles.actionsBar)}
          data-suppress-hover={widgetState.isSuppressTooltip}
        >
          <DirDrillingActionsBar widgetState={widgetState} />
        </div>
      )}
    </div>
  );
});
